import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { getMe } from '../gateway/loginGateway'
import { CurrentUser, getStoredUser, setStoredUser, clearStoredUser } from './currentUser'

type CurrentUserContextValue = {
    user: CurrentUser | null,
    isAdmin: boolean,
    loading: boolean,
    refresh: () => Promise<CurrentUser | null>,
}

const CurrentUserContext = createContext<CurrentUserContextValue>({
    user: null,
    isAdmin: false,
    loading: false,
    refresh: async () => null,
})

export const CurrentUserProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<CurrentUser | null>(getStoredUser())
    const [loading, setLoading] = useState(false)

    const refresh = useCallback(async (): Promise<CurrentUser | null> => {
        if (!localStorage.getItem('token')) return null
        setLoading(true)
        try {
            const response = await getMe()
            const fetchedUser: CurrentUser = response.data
            setStoredUser(fetchedUser)
            setUser(fetchedUser)
            return fetchedUser
        } catch {
            return null
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (localStorage.getItem('token') && !getStoredUser()) {
            refresh()
        }
    }, [refresh])

    return (
        <CurrentUserContext.Provider value={{ user, isAdmin: !!user?.is_admin, loading, refresh }}>
            {children}
        </CurrentUserContext.Provider>
    )
}

export const useCurrentUser = () => useContext(CurrentUserContext)

export { clearStoredUser }
